import React, { useCallback, useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';
import Carousel from 'react-native-reanimated-carousel';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { REDESIGN_COLORS } from '../../styles/colors';
import ImagePlaceholder from '../feed/ImagePlaceholder';
import FallbackImage from './FallbackImage';
import { useResponsive } from '../../hooks/useResponsive';

const MOBILE_HEIGHT = 353;
const DESKTOP_HEIGHT = 321;
const DESKTOP_WIDTH = 560;

/**
 * HeroImageCarousel - Full-bleed image carousel for listing detail.
 *
 * Mobile: uses react-native-reanimated-carousel for swipe gestures.
 * Desktop: manual image switching with hover arrows (avoids gesture conflicts with ScrollView on web).
 *
 * @param {Object} props
 * @param {string[]} props.photoLinks - Array of photo URLs
 * @param {string} [props.modelImageUrl] - Model AI image, used as the per-slide fallback when a dealer photo fails to load
 * @param {Function} [props.onPhotoChange] - Called with current index on slide change
 */
const HeroImageCarousel = ({ photoLinks = [], modelImageUrl = null, onPhotoChange }) => {
  const { isMobile, screenWidth } = useResponsive();
  const [activeIndex, setActiveIndex] = useState(0);
  const [fullscreenVisible, setFullscreenVisible] = useState(false);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const carouselWidth = isMobile ? screenWidth : DESKTOP_WIDTH;
  const carouselHeight = isMobile ? MOBILE_HEIGHT : DESKTOP_HEIGHT;
  const hasMultiplePhotos = photoLinks.length > 1;

  const handleSnapToItem = useCallback(
    (index) => {
      setActiveIndex(index);
      if (onPhotoChange) onPhotoChange(index);
    },
    [onPhotoChange]
  );

  const handleImagePress = useCallback(() => {
    setFullscreenIndex(activeIndex);
    setFullscreenVisible(true);
  }, [activeIndex]);

  const handleFullscreenSnap = useCallback((index) => {
    setFullscreenIndex(index);
  }, []);

  // Desktop arrow navigation
  const handlePrev = useCallback((e) => {
    e?.stopPropagation?.();
    const newIndex = activeIndex > 0 ? activeIndex - 1 : photoLinks.length - 1;
    setActiveIndex(newIndex);
    if (onPhotoChange) onPhotoChange(newIndex);
  }, [activeIndex, photoLinks.length, onPhotoChange]);

  const handleNext = useCallback((e) => {
    e?.stopPropagation?.();
    const newIndex = activeIndex < photoLinks.length - 1 ? activeIndex + 1 : 0;
    setActiveIndex(newIndex);
    if (onPhotoChange) onPhotoChange(newIndex);
  }, [activeIndex, photoLinks.length, onPhotoChange]);

  // Fullscreen arrow navigation
  const handleFullscreenPrev = useCallback((e) => {
    e?.stopPropagation?.();
    setFullscreenIndex((prev) => (prev > 0 ? prev - 1 : photoLinks.length - 1));
  }, [photoLinks.length]);

  const handleFullscreenNext = useCallback((e) => {
    e?.stopPropagation?.();
    setFullscreenIndex((prev) => (prev < photoLinks.length - 1 ? prev + 1 : 0));
  }, [photoLinks.length]);

  if (!photoLinks || photoLinks.length === 0) {
    // No dealer photos: still show the model AI image when we have one (Req 1 AC1 covers
    // an empty/missing URL), else fall back to today's placeholder.
    if (modelImageUrl) {
      return (
        <FallbackImage
          primitive="expo"
          modelImageUrl={modelImageUrl}
          style={{ width: carouselWidth, height: carouselHeight }}
          contentFit="cover"
          cachePolicy="disk"
          transition={200}
          placeholder={require('../../../assets/images/car-placeholder.png')}
        />
      );
    }
    return <ImagePlaceholder style={{ width: carouselWidth, height: carouselHeight }} />;
  }

  const renderItem = ({ item }) => (
    <Pressable
      style={{ width: carouselWidth, height: carouselHeight }}
      onPress={handleImagePress}
    >
      <FallbackImage
        primitive="expo"
        dealerPhotoUrl={item}
        modelImageUrl={modelImageUrl}
        style={{ width: '100%', height: '100%' }}
        contentFit="cover"
        cachePolicy="disk"
        transition={200}
        placeholder={require('../../../assets/images/car-placeholder.png')}
      />
    </Pressable>
  );

  const showDesktopArrows = !isMobile && isHovered && hasMultiplePhotos;
  const dotCount = Math.min(photoLinks.length, 7);

  return (
    <View
      style={[styles.container, !isMobile && styles.containerDesktop]}
      onMouseEnter={!isMobile ? () => setIsHovered(true) : undefined}
      onMouseLeave={!isMobile ? () => setIsHovered(false) : undefined}
    >
      {isMobile ? (
        /* Mobile: swipe-based carousel */
        <Carousel
          width={carouselWidth}
          height={carouselHeight}
          data={photoLinks}
          renderItem={renderItem}
          onSnapToItem={handleSnapToItem}
          loop={hasMultiplePhotos}
          enabled={hasMultiplePhotos}
        />
      ) : (
        /* Desktop: simple image with arrow navigation */
        <Pressable
          style={{ width: carouselWidth, height: carouselHeight }}
          onPress={handleImagePress}
        >
          <FallbackImage
            primitive="expo"
            dealerPhotoUrl={photoLinks[activeIndex]}
            modelImageUrl={modelImageUrl}
            style={{ width: '100%', height: '100%' }}
            contentFit="cover"
            cachePolicy="disk"
            transition={200}
            placeholder={require('../../../assets/images/car-placeholder.png')}
          />
        </Pressable>
      )}

      {/* Top gradient overlay for status bar legibility */}
      {isMobile && (
        <LinearGradient
          colors={['rgba(0,0,0,0.4)', 'transparent']}
          style={styles.topGradient}
          pointerEvents="none"
        />
      )}

      {/* Desktop hover arrows */}
      {showDesktopArrows && (
        <>
          <Pressable style={styles.arrowLeft} onPress={handlePrev}>
            <MaterialCommunityIcons name="chevron-left" size={24} color="#FFFFFF" />
          </Pressable>
          <Pressable style={styles.arrowRight} onPress={handleNext}>
            <MaterialCommunityIcons name="chevron-right" size={24} color="#FFFFFF" />
          </Pressable>
          <View style={styles.dotsRow}>
            {Array.from({ length: dotCount }).map((_, i) => (
              <View
                key={i}
                style={[styles.dot, i === activeIndex % dotCount && styles.dotActive]}
              />
            ))}
          </View>
        </>
      )}

      {/* Photo counter pill */}
      {hasMultiplePhotos && (
        <View style={styles.counterPill}>
          <Text style={styles.counterText}>
            {activeIndex + 1} / {photoLinks.length}
          </Text>
        </View>
      )}

      {/* Fullscreen image viewer */}
      <Modal
        visible={fullscreenVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setFullscreenVisible(false)}
      >
        <View style={styles.fullscreenContainer}>
          {isMobile ? (
            <Carousel
              width={screenWidth}
              height={screenWidth * 0.75}
              data={photoLinks}
              defaultIndex={fullscreenIndex}
              onSnapToItem={handleFullscreenSnap}
              loop={hasMultiplePhotos}
              enabled={hasMultiplePhotos}
              renderItem={({ item, index }) => (
                <View style={{ width: screenWidth, height: screenWidth * 0.75, justifyContent: 'center' }}>
                  <FallbackImage
                    primitive="expo"
                    dealerPhotoUrl={item}
                    modelImageUrl={modelImageUrl}
                    style={{ width: '100%', height: '100%' }}
                    contentFit="contain"
                    cachePolicy="disk"
                  />
                </View>
              )}
            />
          ) : (
            /* Desktop fullscreen: simple image + arrows */
            <View style={{ width: screenWidth, height: screenWidth * 0.75, justifyContent: 'center' }}>
              <FallbackImage
                primitive="expo"
                dealerPhotoUrl={photoLinks[fullscreenIndex]}
                modelImageUrl={modelImageUrl}
                style={{ width: '100%', height: '100%' }}
                contentFit="contain"
                cachePolicy="disk"
              />
            </View>
          )}

          {/* Fullscreen arrows (desktop) */}
          {!isMobile && hasMultiplePhotos && (
            <>
              <Pressable style={styles.fullscreenArrowLeft} onPress={handleFullscreenPrev}>
                <MaterialCommunityIcons name="chevron-left" size={32} color="#FFFFFF" />
              </Pressable>
              <Pressable style={styles.fullscreenArrowRight} onPress={handleFullscreenNext}>
                <MaterialCommunityIcons name="chevron-right" size={32} color="#FFFFFF" />
              </Pressable>
            </>
          )}

          {/* Counter */}
          {hasMultiplePhotos && (
            <View style={styles.fullscreenCounter}>
              <Text style={styles.counterText}>
                {fullscreenIndex + 1} / {photoLinks.length}
              </Text>
            </View>
          )}

          {/* Close button */}
          <Pressable style={styles.closeButton} onPress={() => setFullscreenVisible(false)}>
            <MaterialCommunityIcons name="close" size={24} color="#FFFFFF" />
          </Pressable>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    overflow: 'hidden',
  },
  containerDesktop: {
    borderRadius: 8,
    width: DESKTOP_WIDTH,
  },
  topGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 80,
  },
  counterPill: {
    position: 'absolute',
    bottom: 62,
    right: 12,
    backgroundColor: REDESIGN_COLORS.photoCounterBg,
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  counterText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 12,
    color: REDESIGN_COLORS.textPrimary,
  },

  // Desktop hover arrows
  arrowLeft: {
    position: 'absolute',
    left: 12,
    top: '50%',
    marginTop: -20,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  arrowRight: {
    position: 'absolute',
    right: 12,
    top: '50%',
    marginTop: -20,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dotsRow: {
    position: 'absolute',
    bottom: 12,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
  },
  dotActive: {
    backgroundColor: '#FFFFFF',
  },

  // Fullscreen viewer
  fullscreenContainer: {
    flex: 1,
    backgroundColor: '#000000',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fullscreenCounter: {
    position: 'absolute',
    bottom: 48,
    alignSelf: 'center',
    backgroundColor: REDESIGN_COLORS.photoCounterBg,
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  fullscreenArrowLeft: {
    position: 'absolute',
    left: 24,
    top: '50%',
    marginTop: -24,
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fullscreenArrowRight: {
    position: 'absolute',
    right: 24,
    top: '50%',
    marginTop: -24,
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButton: {
    position: 'absolute',
    top: 48,
    right: 16,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default HeroImageCarousel;
